/*
  File: assets/ozplug-header.js
  Purpose: Dawn-safe OZPLUG custom header interactions
*/

(function () {
  'use strict';

  if (window.OZPlugHeaderReady) return;
  window.OZPlugHeaderReady = true;

  const ROOT_SELECTOR = "[data-oz-header]";
  const INITIALIZED_ATTRIBUTE = "data-oz-header-initialized";
  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");

  function initHeader(root) {
    if (!root || root.hasAttribute(INITIALIZED_ATTRIBUTE)) return;

    root.setAttribute(INITIALIZED_ATTRIBUTE, "true");

    const overlay = root.querySelector("[data-oz-overlay]");
    const drawers = Array.from(root.querySelectorAll("[data-oz-drawer]"));
    const openers = Array.from(root.querySelectorAll("[data-oz-open-drawer]"));
    const closers = Array.from(root.querySelectorAll("[data-oz-close-drawer]"));
    const mobileSwitches = Array.from(root.querySelectorAll("[data-oz-mobile-switch]"));
    const desktopDropdowns = Array.from(root.querySelectorAll("[data-oz-desktop-dropdown]"));

    let activeDrawer = null;
    let lastFocusedElement = null;

    function getDrawer(name) {
      return root.querySelector(`[data-oz-drawer="${name}"]`);
    }

    function getPrimaryOpener(name) {
      return root.querySelector(`[data-oz-open-drawer="${name}"]`);
    }

    function setDrawerA11yState(drawer, isOpen) {
      if (!drawer) return;

      drawer.setAttribute("aria-hidden", String(!isOpen));

      /*
        Keep closed off-canvas drawers out of the keyboard order.
        `aria-hidden` helps screen readers, while `inert` prevents hidden links/buttons
        from being reached by Tab without changing the visual design.
      */
      if ("inert" in drawer) {
        drawer.inert = !isOpen;
      }
    }

    drawers.forEach(function (drawer) {
      setDrawerA11yState(drawer, false);
    });

    function updateOpeners(name, expanded) {
      openers.forEach(function (button) {
        if (button.dataset.ozOpenDrawer === name) {
          button.setAttribute("aria-expanded", String(expanded));
        } else if (expanded) {
          button.setAttribute("aria-expanded", "false");
        }
      });
    }

    function openDrawer(name, options) {
      const drawer = getDrawer(name);

      if (!drawer) return;

      if (activeDrawer && activeDrawer.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      closeDrawer({ restoreFocus: false });

      lastFocusedElement =
        options && options.opener
          ? options.opener
          : document.activeElement;

      activeDrawer = drawer;
      drawer.classList.add("is-open");
      setDrawerA11yState(drawer, true);

      if (overlay) {
        overlay.classList.add("is-open");
        overlay.setAttribute("aria-hidden", "false");
      }

      document.body.classList.add("oz-header-lock");
      updateOpeners(name, true);

      if (name === "cart") {
        loadCart(root);
      }

      window.requestAnimationFrame(function () {
        const preferredFocus =
          name === "search"
            ? drawer.querySelector("[data-oz-search-input]")
            : drawer.querySelector(FOCUSABLE_SELECTOR);

        (preferredFocus || drawer).focus();
      });
    }

    function closeDrawer(options) {
      const shouldRestoreFocus =
        !options || options.restoreFocus !== false;
      const focusTarget =
        shouldRestoreFocus &&
        lastFocusedElement &&
        document.contains(lastFocusedElement) &&
        typeof lastFocusedElement.focus === "function"
          ? lastFocusedElement
          : null;

      /* Move focus out before applying aria-hidden/inert to the open drawer. */
      if (focusTarget && !focusTarget.closest('[data-oz-drawer]')) {
        focusTarget.focus({ preventScroll: true });
      }

      drawers.forEach(function (drawer) {
        drawer.classList.remove("is-open");
        setDrawerA11yState(drawer, false);
      });

      if (overlay) {
        overlay.classList.remove("is-open");
        overlay.setAttribute("aria-hidden", "true");
      }

      document.body.classList.remove("oz-header-lock");
      openers.forEach(function (button) {
        button.setAttribute("aria-expanded", "false");
      });

      activeDrawer = null;
    }

    function trapFocus(event) {
      if (!activeDrawer || event.key !== "Tab") return;

      const focusableElements = Array.from(
        activeDrawer.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter(function (element) {
        return !element.hasAttribute("hidden") &&
          element.offsetParent !== null;
      });

      if (!focusableElements.length) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openers.forEach(function (button) {
      button.addEventListener("click", function () {
        openDrawer(button.dataset.ozOpenDrawer, { opener: button });
      });
    });

    closers.forEach(function (button) {
      button.addEventListener("click", function () {
        closeDrawer();
      });
    });

    mobileSwitches.forEach(function (button) {
      button.addEventListener("click", function () {
        const targetDrawer = button.dataset.ozMobileSwitch;
        openDrawer(targetDrawer, {
          opener: getPrimaryOpener(targetDrawer) || button
        });
      });
    });

    overlay?.addEventListener("click", function () {
      closeDrawer();
    });

    root.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && activeDrawer) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      trapFocus(event);
    });

    root.addEventListener("click", function (event) {
      const link = event.target.closest(
        '[data-oz-drawer="mobile"] a[href]'
      );

      if (link) {
        closeDrawer({ restoreFocus: false });
      }
    });

    document.addEventListener("click", function (event) {
      if (!root.contains(event.target)) {
        desktopDropdowns.forEach(function (details) {
          details.removeAttribute("open");
        });
      }
    });

    desktopDropdowns.forEach(function (details) {
      details.addEventListener("toggle", function () {
        if (!details.open) return;

        desktopDropdowns.forEach(function (otherDetails) {
          if (otherDetails !== details) {
            otherDetails.removeAttribute("open");
          }
        });
      });
    });

    root.addEventListener("click", function (event) {
      const removeButton = event.target.closest("[data-oz-remove-line]");

      if (!removeButton) return;

      const line = Number(removeButton.dataset.ozRemoveLine);

      if (!Number.isInteger(line) || line < 1) return;

      removeButton.disabled = true;
      changeCartLine(root, line, 0).catch(function () {
        removeButton.disabled = false;
      });
    });

    root.ozHeaderApi = {
      openDrawer: openDrawer,
      closeDrawer: closeDrawer,
      openCart: function () {
        openDrawer("cart");
      },
      loadCart: function (options) {
        return loadCart(root, options);
      },
      refreshCart: function (options) {
        return loadCart(root, options || { silent: true });
      }
    };

    window.OZPlugHeaderCart = {
      open: root.ozHeaderApi.openCart,
      refresh: root.ozHeaderApi.refreshCart
    };

    loadCart(root, { silent: true });

    document.addEventListener("cart:updated", function (event) {
      const cart = event.detail && event.detail.cart;

      if (cart) {
        renderCart(root, cart);
      } else {
        loadCart(root, { silent: true });
      }
    });

    document.addEventListener("cart:refresh", function (event) {
      const cart = event.detail && event.detail.cart;

      if (cart) {
        renderCart(root, cart);
      } else {
        loadCart(root, { silent: true });
      }
    });

    document.addEventListener("ozplug:cart-added", function () {
      loadCart(root, { silent: true }).then(function () {
        if (root.dataset.openCartAfterAdd === "true") {
          openDrawer("cart");
        }
      });
    });

    document.addEventListener("ozplug:open-cart", function () {
      openDrawer("cart");
    });
  }

  async function loadCart(root, options) {
    const silent = options && options.silent;
    const status = root.querySelector("[data-oz-cart-status]");
    const cartUrl = root.dataset.cartUrl || "/cart.js";

    if (!silent && status) {
      status.hidden = false;
      status.textContent = "Loading cart…";
    }

    try {
      const response = await fetch(cartUrl, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!response.ok) {
        throw new Error("Cart request failed.");
      }

      const cart = await response.json();
      renderCart(root, cart);
      return cart;
    } catch (error) {
      showCartError(root);
      throw error;
    }
  }

  async function changeCartLine(root, line, quantity) {
    const changeUrl = root.dataset.cartChangeUrl || "/cart/change.js";

    const response = await fetch(changeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        line: line,
        quantity: quantity
      })
    });

    const cart = await response.json();

    if (!response.ok) {
      showCartError(root);
      throw new Error(cart.description || "Cart update failed.");
    }

    renderCart(root, cart);

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { cart: cart }
      })
    );

    return cart;
  }

  function renderCart(root, cart) {
    const status = root.querySelector("[data-oz-cart-status]");
    const itemsContainer = root.querySelector("[data-oz-cart-items]");
    const emptyState = root.querySelector("[data-oz-cart-empty]");
    const footer = root.querySelector("[data-oz-cart-footer]");
    const subtotal = root.querySelector("[data-oz-cart-subtotal]");
    const cartSummary = root.querySelector("[data-oz-cart-summary]");
    const itemCount = Number(cart.item_count) || 0;

    updatePlugPointsMessage(root, cart);

    updateCartCount(root, itemCount);
    updateCartSummary(cartSummary, itemCount);
    updateFreeShippingProgress(root, cart);

    if (status) {
      status.hidden = true;
    }

    if (!cart.items || cart.items.length === 0) {
      if (itemsContainer) {
        itemsContainer.hidden = true;
        itemsContainer.innerHTML = "";
      }

      if (emptyState) emptyState.hidden = false;
      if (footer) footer.hidden = true;
      updatePlugPointsMessage(root, null);
      return;
    }

    if (emptyState) emptyState.hidden = true;

    if (itemsContainer) {
      itemsContainer.innerHTML = cart.items
        .map(function (item, index) {
          return renderCartItem(root, item, index + 1, cart.currency);
        })
        .join("");
      itemsContainer.hidden = false;
    }

    if (subtotal) {
      subtotal.textContent = formatMoney(
        cart.total_price,
        cart.currency
      );
    }

    if (footer) footer.hidden = false;
  }

  function renderCartItem(root, item, line, currency) {
    const itemUrl = getSafeCartUrl(item.url, "/cart");
    const imageUrl = getSafeImageUrl(resizeShopifyImage(item.image, 160));
    const imageMarkup = imageUrl
      ? `
        <a class="oz-header-cart-item__image" href="${escapeAttribute(itemUrl)}">
          <img
            src="${escapeAttribute(imageUrl)}"
            alt="${escapeAttribute(item.product_title || item.title)}"
            width="72"
            height="72"
            loading="lazy"
          >
        </a>
      `
      : `
        <div class="oz-header-cart-item__placeholder" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <path d="m21 15-5-5L5 21"/>
          </svg>
        </div>
      `;

    const variantTitle =
      item.variant_title && item.variant_title !== "Default Title"
        ? `<p class="oz-header-cart-item__variant">${escapeHTML(item.variant_title)}</p>`
        : "";

    return `
      <article class="oz-header-cart-item">
        ${imageMarkup}

        <div class="oz-header-cart-item__content">
          <a class="oz-header-cart-item__title" href="${escapeAttribute(itemUrl)}">
            ${escapeHTML(item.product_title || item.title)}
          </a>

          ${variantTitle}

          <div class="oz-header-cart-item__meta">
            <span>Qty ${Number(item.quantity) || 1}</span>
            <strong>${formatMoney(item.final_line_price, currency)}</strong>
          </div>

          <button
            class="oz-header-cart-remove"
            type="button"
            data-oz-remove-line="${line}"
          >
            ${escapeHTML(root.dataset.removeLabel || "Remove")}
          </button>
        </div>
      </article>
    `;
  }

  function updateCartCount(root, count) {
    root.querySelectorAll("[data-oz-cart-count]").forEach(function (badge) {
      badge.textContent = String(count);
      badge.hidden = count < 1;
    });

    document.querySelectorAll("[data-cart-count]").forEach(function (badge) {
      badge.textContent = String(count);
      badge.hidden = count < 1;
    });
  }

  function showCartError(root) {
    const status = root.querySelector("[data-oz-cart-status]");
    const itemsContainer = root.querySelector("[data-oz-cart-items]");
    const emptyState = root.querySelector("[data-oz-cart-empty]");
    const footer = root.querySelector("[data-oz-cart-footer]");
    const freeShippingProgress = root.querySelector("[data-oz-free-shipping-progress]");
    const cartSummary = root.querySelector("[data-oz-cart-summary]");

    if (status) {
      status.hidden = false;
      status.textContent =
        root.dataset.cartError ||
        "The cart could not be loaded. Please try again.";
    }

    if (itemsContainer) itemsContainer.hidden = true;
    if (emptyState) emptyState.hidden = true;
    if (footer) footer.hidden = true;
    if (cartSummary) cartSummary.hidden = true;
    if (freeShippingProgress) freeShippingProgress.hidden = true;
    updatePlugPointsMessage(root, null);
  }

  function updateCartSummary(summary, itemCount) {
    if (!summary) return;

    const count = Math.max(0, Number(itemCount) || 0);
    const label = count === 1 ? "item" : "items";
    const countElement = summary.querySelector("[data-oz-cart-summary-count]");

    if (countElement) {
      countElement.textContent = `${count} ${label}`;
    }

    summary.hidden = false;
  }

  function updateFreeShippingProgress(root, cart) {
    const progress = root.querySelector("[data-oz-free-shipping-progress]");

    if (!progress || root.dataset.freeShippingEnabled !== "true") return;

    const threshold = Number(root.dataset.freeShippingThreshold) || 10000;
    const total = Math.max(0, Number(cart && cart.total_price) || 0);
    const remaining = Math.max(0, threshold - total);
    const percentage = threshold > 0 ? Math.min(100, (total / threshold) * 100) : 100;
    const currency = cart && cart.currency;
    const message = progress.querySelector("[data-oz-free-shipping-message]");
    const bar = progress.querySelector("[data-oz-free-shipping-bar]");
    const fill = progress.querySelector("[data-oz-free-shipping-fill]");
    const target = progress.querySelector("[data-oz-free-shipping-target]");
    const milestones = progress.querySelectorAll("[data-oz-free-shipping-milestone]");

    progress.hidden = false;
    progress.classList.toggle("is-complete", remaining === 0 && total > 0);

    if (message) {
      if (remaining === 0 && total > 0) {
        message.textContent = root.dataset.freeShippingSuccessText || "You unlocked free shipping!";
      } else if (total === 0) {
        message.textContent = formatTemplate(
          root.dataset.freeShippingEmptyText || "Add [amount] more to unlock free shipping.",
          remaining,
          currency
        );
      } else {
        message.textContent = formatTemplate(
          root.dataset.freeShippingRemainingText || "You are [amount] away from free shipping.",
          remaining,
          currency
        );
      }
    }

    if (bar) {
      bar.setAttribute("aria-valuemax", String(threshold));
      bar.setAttribute("aria-valuenow", String(Math.min(total, threshold)));
      bar.setAttribute("aria-valuetext", message ? message.textContent : "Free shipping progress");
    }

    if (fill) {
      fill.style.width = `${percentage}%`;
    }

    if (target) {
      target.textContent = formatMoneyCompact(threshold, currency);
    }

    milestones.forEach(function (milestone) {
      const milestoneValue = Number(milestone.dataset.milestoneCents) || 0;
      milestone.classList.toggle("is-active", total >= milestoneValue && milestoneValue > 0);
    });
  }

  function updatePlugPointsMessage(root, cart) {
    const box = root.querySelector("[data-oz-cart-plug-points]");
    const value = root.querySelector("[data-oz-cart-plug-points-value]");
    const name = root.querySelector("[data-oz-cart-plug-points-name]");

    if (!box) return;

    const total = cart ? Math.max(0, Number(cart.total_price) || 0) : 0;
    const rate = Math.max(0, Number(root.dataset.plugPointsRate) || 0);
    const pointsName = root.dataset.plugPointsName || "Plug Points";
    const points = Math.floor((total / 100) * rate);

    if (!cart || !cart.items || cart.items.length === 0 || points <= 0) {
      box.hidden = true;
      return;
    }

    if (value) value.textContent = String(points);
    if (name) name.textContent = pointsName;
    box.hidden = false;
  }

  function formatTemplate(template, cents, currency) {
    return String(template || "").replaceAll("[amount]", formatMoney(cents, currency));
  }

  function formatMoney(cents, currency) {
    return formatMoneyWithOptions(cents, currency, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function formatMoneyCompact(cents, currency) {
    return formatMoneyWithOptions(cents, currency, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  function formatMoneyWithOptions(cents, currency, options) {
    const amount = (Number(cents) || 0) / 100;
    const activeCurrency =
      currency ||
      (window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active) ||
      "USD";

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: activeCurrency,
        ...options
      }).format(amount);
    } catch (error) {
      return options && options.maximumFractionDigits === 0
        ? `$${Math.round(amount)}`
        : `$${amount.toFixed(2)}`;
    }
  }

  function getSafeCartUrl(value, fallback) {
    const fallbackUrl = fallback || "/cart";
    const rawValue = String(value || "").trim();

    if (!rawValue) return fallbackUrl;

    try {
      const parsedUrl = new URL(rawValue, window.location.origin);
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") return fallbackUrl;

      if (parsedUrl.origin === window.location.origin) {
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      }

      return fallbackUrl;
    } catch (error) {
      return fallbackUrl;
    }
  }

  function getSafeImageUrl(value) {
    const rawValue = String(value || "").trim();

    if (!rawValue) return "";

    try {
      const parsedUrl = new URL(rawValue, window.location.origin);
      if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") return "";

      return parsedUrl.href;
    } catch (error) {
      return "";
    }
  }

  function resizeShopifyImage(url, width) {
    if (!url || typeof url !== "string") return "";

    if (url.includes("width=")) {
      return url.replace(/width=\d+/, `width=${width}`);
    }

    return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
  }

  function escapeHTML(value) {
    return String(value == null ? "" : value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function escapeAttribute(value) {
    return escapeHTML(value);
  }

  function initAll(context) {
    (context || document)
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initHeader);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    const root = event.target.querySelector(ROOT_SELECTOR);

    if (root && root.ozHeaderApi) {
      root.ozHeaderApi.closeDrawer({ restoreFocus: false });
    }
  });
})();
